import type {
  GoalContext,
  GoalPriority,
  GoalStatus,
  GoalVisibility,
} from "@/lib/types";
import { goalVisibilityOptions } from "./visibilityVocabulary";

/**
 * Shared label maps + option lists for player-goal enums.
 *
 * One source of truth for the goal status / visibility / priority
 * / context display labels — used by the M6a `GoalComposerModal`
 * (the `<select>` options) and the M7 `GoalCard` (the meta line +
 * status badge + the coach-side status dropdown).
 *
 * Keys mirror the backend `_VALID_GOAL_*` sets (see `models.py`).
 */

export const GOAL_STATUS_OPTIONS: ReadonlyArray<{
  value: GoalStatus;
  label: string;
}> = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In progress" },
  { value: "needs_follow_up", label: "Needs follow-up" },
  { value: "achieved", label: "Achieved" },
  { value: "archived", label: "Archived" },
];

// One vocabulary for every surface — see visibilityVocabulary.ts.
export const GOAL_VISIBILITY_OPTIONS: ReadonlyArray<{
  value: GoalVisibility;
  label: string;
}> = goalVisibilityOptions();

export const GOAL_PRIORITY_OPTIONS: ReadonlyArray<{
  value: GoalPriority;
  label: string;
}> = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

export const GOAL_CONTEXT_OPTIONS: ReadonlyArray<{
  value: GoalContext;
  label: string;
}> = [
  { value: "next_match", label: "Next match" },
  { value: "next_training", label: "Next training" },
  { value: "season_goal", label: "Season goal" },
  { value: "other", label: "Other" },
];

/** The statuses that count as "active" — drives whether a goal
 * card renders muted (mirrors legacy `ACTIVE_GOAL_STATUSES`). */
export const ACTIVE_GOAL_STATUSES: ReadonlySet<GoalStatus> = new Set([
  "open",
  "in_progress",
  "needs_follow_up",
]);

const labelMap = <T extends string>(
  opts: ReadonlyArray<{ value: T; label: string }>,
): Record<string, string> =>
  Object.fromEntries(opts.map((o) => [o.value, o.label]));

const STATUS_LABELS = labelMap(GOAL_STATUS_OPTIONS);
const VISIBILITY_LABELS = labelMap(GOAL_VISIBILITY_OPTIONS);
const PRIORITY_LABELS = labelMap(GOAL_PRIORITY_OPTIONS);
const CONTEXT_LABELS = labelMap(GOAL_CONTEXT_OPTIONS);

export const goalStatusLabel = (s?: string) =>
  (s && STATUS_LABELS[s]) || "Open";
export const goalVisibilityLabel = (v?: string) =>
  (v && VISIBILITY_LABELS[v]) || "Player + family";
export const goalPriorityLabel = (p?: string) =>
  (p && PRIORITY_LABELS[p]) || "Medium";
export const goalContextLabel = (c?: string) =>
  (c && CONTEXT_LABELS[c]) || "Goal";
